<script setup lang="ts">
  import type { Editor } from "@tiptap/vue-3";
  import type { HTMLAttributes } from "vue";
  import { cn } from "@/lib/utils";
  import { computed, onMounted, ref, watch } from "vue";
  import { useTiptapContext } from ".";

  const props = defineProps<{
    editor?: Editor | null;
    class?: HTMLAttributes["class"];
    placeholder?: string;
  }>();

  // Get editor from context if not provided directly
  const { editor: contextEditor } = useTiptapContext();
  const editor = computed(() => props.editor ?? contextEditor.value);

  // Editor is ready when it's available and initialized
  const isEditorReady = computed(() => {
    return editor.value && editor.value.isEditable;
  });

  // Reference to the editor element
  const editorElement = ref<HTMLElement | null>(null);

  // Mount editor to DOM when editor is available or element changes
  watch(
    [editor, editorElement],
    ([currentEditor, element]) => {
      if (currentEditor && element && !element.firstChild) {
        // If editor is ready but not yet mounted to this element
        element.append(currentEditor.view.dom);
      }
    },
    { immediate: true },
  );

  // Also mount on component mount in case both are already available
  onMounted(() => {
    if (isEditorReady.value && editorElement.value && !editorElement.value.firstChild) {
      editorElement.value.append(editor.value!.view.dom);
    }

    // Add ARIA attributes after mounting (firstChild now exists)
    if (isEditorReady.value && editorElement.value?.firstChild) {
      const editorDOM = editorElement.value.firstChild as HTMLElement;
      editorDOM.setAttribute("role", "textbox");
      editorDOM.setAttribute("aria-multiline", "true");
      editorDOM.setAttribute("aria-label", "Rich text editor");

      // Add content description for screen readers
      const srDescription = document.createElement("span");
      srDescription.className = "sr-only";
      srDescription.textContent =
        "Use keyboard shortcuts like Ctrl+B for bold, Ctrl+I for italic. Press Alt+F10 for the toolbar.";
      editorElement.value.appendChild(srDescription);
    }
  });
</script>

<template>
  <div
    :class="cn('tiptap-editor-wrapper relative', props.class)"
    data-slot="tiptap-content"
    aria-label="Rich text editor">
    <div
      v-if="isEditorReady"
      ref="editorElement"
      class="h-full w-full focus:outline-none px-3 py-1.5 text-13 leading-snug [&_.ProseMirror]:outline-none [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1 [&_li]:my-0.5"
      role="region" />

    <div
      v-else
      class="tiptap-editor-placeholder h-full w-full flex items-center justify-center text-muted-foreground text-13 italic"
      aria-live="polite">
      {{ placeholder || "Loading editor..." }}
    </div>
  </div>
</template>

<style>
  @reference "../../../assets/styles/shadcn.css";

  /* Placeholder - pseudo-elements can't be styled with inline Tailwind classes */
  .tiptap-editor-wrapper .ProseMirror p.is-editor-empty:first-child::before {
    @apply text-muted-foreground float-left h-0 pointer-events-none;
    content: attr(data-placeholder);
  }
</style>
